// services/api/index.js

import axios from "../axios/axiosInstance";
import ApiURL from "../ApiURL";
import { removeEmptyKeys } from "utils";

/**
 * Build the full API endpoint.
 */
const buildUrl = (key, id = null) => {
  const base = ApiURL[key];
  if (!base) throw new Error(`Missing API key: ${key}`);
  return id ? `${base}/${id}` : base;
};

/**
 * Format query params using URLSearchParams.
 */
const buildQuery = (params = {}) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((v) => query.append(key, v));
    } else {
      query.append(key, val);
    }
  });

  return query.toString() ? `?${query.toString()}` : "";
};

/**
 * Make the API call. `config` (e.g. `{ signal }` from callApi) goes to
 * axios as-is, so a cancelled call aborts its request.
 */
const call = ({ url, method, data, config }) => {
  const { cancelToken, ...rest } = config && typeof config === "object" ? config : {};
  return axios({
    ...rest,
    // Several helpers fill `{ cancelToken }` from an argument that is really
    // something else (a payload, `{}`); only a real axios CancelToken is kept
    ...(typeof cancelToken?.subscribe === "function" ? { cancelToken } : {}),
    url,
    method,
    data,
  });
};

/**
 * GET
 */
export const apiGet = (
  key,
  queryParams = {},
  id = null,
  config = {},
  addUrl = "",
) => {
  const params = removeEmptyKeys(queryParams);
  const url = `${buildUrl(key, id)}${addUrl}${buildQuery(params)}`;
  return call({ url, method: "GET", config });
};

/**
 * POST
 */
export const apiPost = (
  key,
  data = {},
  id = null,
  config = {},
  addUrl = "",
) => {
  const url = `${buildUrl(key, id)}${addUrl}`;
  let params = data;
  if (!(data instanceof FormData)) {
    params = removeEmptyKeys(data);
  }
  return call({ url, method: "POST", data: params, config });
};

/**
 * PUT
 */
export const apiPut = (
  key = null,
  id = null,
  data = {},
  config = {},
  addUrl = "",
) => {
  const url = `${buildUrl(key, id)}${addUrl}`;
  let params = data;
  if (!(params instanceof FormData)) {
    params = removeEmptyKeys(data);
  }

  return call({ url, method: "PUT", data: params, config });
};

/**
 * PATCH
 */
export const apiPatch = (key, id, addUrl = "", data = {}, config = {}) => {
  const url = `${buildUrl(key, id)}${addUrl}`;
  let params = data;

  if (!(data instanceof FormData)) {
    params = removeEmptyKeys(data);
  }
  return call({ url, method: "PATCH", data: params, config });
};

/**
 * DELETE
 */
export const apiDelete = (key, id, data = {}, config = {}, addUrl = "") => {
  const url = `${buildUrl(key, id)}${addUrl}`;
  return call({ url, method: "DELETE", data, config });
};
